import type { FormEventHandler, MouseEventHandler, ReactNode } from 'react';
import { forwardRef } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import type { ClassNameProps } from '@tripleten/cn';

import { SocialButtons } from 'src/shared/components/social-buttons/social-buttons.js';
import type {
    KcContext_LoginUsername,
    KcContext_MagicLinkConfirm,
    KcContext_MagicLinkForm,
} from 'src/shared/utils/kc-context/kc-context.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { cnForm } from './kc-form.constants.js';

import './form.css';

export interface FormProps extends ClassNameProps {
    action: string;
    onSubmit?: FormEventHandler<HTMLFormElement>;
    kcContext:
        | KcContext.LoginVerifyEmail
        | KcContext.Register
        | KcContext.Login
        | KcContext.LoginResetPassword
        | KcContext.LoginUpdatePassword
        | KcContext.LoginOtp
        | KcContext.LoginConfigTotp
        | KcContext_LoginUsername
        | KcContext_MagicLinkConfirm
        | KcContext_MagicLinkForm
        | KcContext.LoginUpdateProfile;
    children: ReactNode;
    onSocialClick?: MouseEventHandler<HTMLAnchorElement>;
    title?: string;
}

export const Form = forwardRef<HTMLFormElement, FormProps>(
    ({ kcContext, children, className, title, ...props }, outerRef) => {
        const { pageId, client, realm } = kcContext;

        const isClientHR = client.clientId === 'hr-admin' || client.clientId === 'prestable-hr-admin';
        const isUsernameLoginVisible = realm.displayName !== 'internal';

        const isSocialBlockVisible =
            pageId === 'register.ftl' ||
            pageId === 'magic-link-form.ftl' ||
            pageId === 'login.ftl' ||
            pageId === 'login-username.ftl';

        const { msgStr } = useGetMsg(kcContext);
        const socialButtonTitle = msgStr('socialButtonTitle');
        const loginTitle = title ?? msgStr('loginTitle');

        return (
            <div className={cnForm({}, [className])}>
                <h1 className={cnForm('title')}>{title || loginTitle}</h1>

                {isUsernameLoginVisible && (
                    <form className={cnForm('content')} method="post" ref={outerRef} {...props}>
                        {children}
                    </form>
                )}

                {isSocialBlockVisible && 'social' in kcContext && kcContext.social && !isClientHR && (
                    <div className={cnForm('content')}>
                        <SocialButtons social={kcContext.social} buttonTitle={socialButtonTitle} {...props} />
                    </div>
                )}
            </div>
        );
    },
);
